import React, { useContext, useEffect, useState } from "react";
import styles from "../../../form.module.css";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Spin,
  Typography,
} from "antd";
import useFetch from "../../../../../hooks/useFetch";
import useToast from "../../../../../hooks/useToast";
import {
  eFORM_FOR,
  eFORMSTATUS,
  eResultCode,
  eYES_OR_NO,
  INPUT_LENGTH,
} from "../../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import Text from "../../../../../context/language-context";
import { Table } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GetFormData,
  getTimeline,
  updateField,
  UploadDocs,
} from "../../../../../utils/api.constant";
import AuthUtil from "../../../../../utils/auth";
import { FaCheck } from "react-icons/fa";
import { LoaderContext } from "../../../../../context/loader";
import { Rule } from "antd/es/form";
import {
  getNumberInLanguage,
  stringDateInLanguage,
} from "../../../../../utils/helperFunctions";

import EncryptUtils from "../../../../../utils/encrypt";
import {
  allowOnlyNumericInput_onKeyDown,
  disableFutureDates_on_input_date_max,
  numberInputRules,
  validateFile,
} from "../../../../../utils/input.validator";
import { Regex } from "../../../../../utils/validation";
import { TFormProps } from "../../../../../types";
import { UserContext } from "../../../../../context";
import { eDocType } from "../../../../../utils/enum";
import Utils from "../../../../../utils";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

/**
 * SchoolPartOne component is a form for collecting information about a school.
 * It includes various fields for inputting details about the school, its head,
 * trustees, sponsors, and other relevant information.
 *
 * @param {TFormProps} props - The properties passed to the component.
 * @param {boolean} props.disabled - Indicates if the form fields should be disabled.
 * @param {string} props.currentFormId - The current form ID.
 * @param {string} props.currentUfmId - The current user form mapping ID.
 *
 * @returns {JSX.Element} The rendered SchoolPartOne component.
 *
 * @component
 * @example
 * <SchoolPartOne disabled={false} currentFormId="form123" currentUfmId="ufm123" />
 *
 * @remarks
 * This component uses various hooks and contexts such as `useFetch`, `useToast`,
 * `useSearchParams`, `useNavigate`, `UserContext`, `LanguageContext`, and `LoaderContext`.
 * It also includes multiple `useEffect` hooks to handle side effects and form data fetching.
 *
 * The form includes fields for:
 * - School name and address
 * - Head of the institution's details
 * - Trustee and sponsor details
 * - Various tables for inputting data related to teachers, students, and achievements
 * - An affidavit declaration section
 *
 * The form submission is handled by the `onFinish` function, which sends the form data
 * to the server using the `post` method from `useFetch`. The `handleFieldChange` function
 * is used to handle changes to individual form fields and update the server accordingly.
 *
 * The component also includes functions for uploading documents and calculating totals
 * for table fields.

 */
 type DATA_TYPE_SCHOOL_PT1_Q29 = {
  key: any;
  label: string;
  ved: string;
  vedShakha1: string;
  vedShakha2?: string;
  vedShakha3?: string;
  vedShakha4?: string;
};

 type DATA_TYPE_SCHOOL_PT1_Q30 = {
  vedShakha: string;
  fullTimeTeacher: string;
  partTimeTeacher: string;
  totalTeacher: string;
};

 type DATA_TYPE_SCHOOL_PT1_Q32 = {
  vedShakha: string;
  moolantStudents: string;
  level1Students: string;
  level2Students: string;
  beyondLevel2Students: string;
  totalVedStudents: string;
};

 type DATA_TYPE_SCHOOL_PT1_Q33 = {
  studentStartSession: string;
  studentCompletedStudy: string;
  newVedicStudent: string;
  studentDroppedOut: string;
  studentEndSession: string;
};

 type DATA_TYPE_SCHOOL_PT1_Q34 = {
  level: any;
  moolpathi: number | string;
  level1: number | string;
  level2: number | string;
  beyondLevel2: number | string;
  total: number | string;
};

const SchoolPartOne = ({
  disabled,
  currentFormId,
  currentUfmId,
}: TFormProps) => {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formId, setFormId] = useState<string | null>(null);
  const [ufmId, setUfmId] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const { user } = useContext(UserContext);
  const [documentsData, setDocumentsData] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureId, setSignatureId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [applyPdfStyles, setApplyPdfStyles] = useState(false);

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;
  const PREVIEW = Text({ tid: "PREVIEW", def: "Preview" });
  const OTHER = Text({ tid: "ANYA", def: "ANYA" });

  const [isUploading, setIsUploading] = useState<Boolean>(false);

  useEffect(() => {
    if (searchParams) {
      // Decrypt and set formId, ufmId, and formFor from URL search parameters
      const formId = EncryptUtils.decrypt(searchParams.get("id") || "");
      const ufmId = EncryptUtils.decrypt(searchParams.get("ufmid") || "");
      const formFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      setFormId(formId);
      setUfmId(ufmId);
      setFormFor(formFor);
    }
    if (currentFormId && currentUfmId) {
      // Decrypt and set formId and ufmId from props if available
      console.log("currentFormId", currentFormId);  
      setFormId(EncryptUtils.decrypt(currentFormId));
      setUfmId(EncryptUtils.decrypt(currentUfmId));
    }
  }, [searchParams, currentFormId, currentUfmId]);

  const handleFieldChange = async (
    field: string,
    value: any,
    bulkPayload?: any,
    isSubmitted?: boolean
  ) => {
    if (formId?.length && ufmId?.length) {
      try {
        setIsSubmitting(true);
        const payload: any = {
          data: {
            id: formId,
            userFormMappingId: ufmId,
          },
        };

        if (field.length > 0) {
          const trimmedValue = value.trim();
          if (trimmedValue.length) {
            payload.data[field] = trimmedValue || "";
          } else {
            form.setFieldValue(field, "");
            return;
          }
        }
        if (bulkPayload) {
          payload.data = {
            ...payload.data,
            ...bulkPayload,
          };
        }
        if (isSubmitted) {
          payload.data.isSubmitted = true;
        }
        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const uploadDocument = async (
    file: File,
    type: eDocType,
    documentName?: string
  ) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", type.toString());
      formData.append("ufmId", ufmId || "");
      formData.append("userId", user?.id?.toString() || "");
      formData.append("labelName", documentName || "");
      formData.append("currentStage", eFORMSTATUS.STAGE1INPROGRESS.toString());
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        }
      );
      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode, description } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        setIsUploading(false);
        switch (documentName) {
          case "SCHOOL_PT1_Q43":
            setSignatureId(data.docUrl.toString());
            break;
          default:
            break;
        }
        return data.docUrl.toString();
      } else {
        setIsUploading(false);
        console.error("Failed to upload images", response.statusText);
      }
    } catch (error) {
      setIsUploading(false);
      console.error(error);
    }
  };

  useEffect(() => {
    const documentFields = [{ id: signatureId, field: "SCHOOL_PT1_Q43" }];

    documentFields.forEach(({ id, field }) => {
      if (id) {
        handleFieldChange(field, id);
      }
    });
  }, [signatureId]);

  useEffect(() => {
    form.setFieldsValue({
      SCHOOL_PT1_Q30: DATA_SOURCE_SCHOOL_PT1_Q30,
      SCHOOL_PT1_Q32: DATA_SOURCE_SCHOOL_PT1_Q32,
      SCHOOL_PT1_Q33: DATA_SOURCE_SCHOOL_PT1_Q33,
      SCHOOL_PT1_Q34: DATA_SOURCE_SCHOOL_PT1_Q34,
    });
  }, [form]);

  const handleGeneratePDF = (id: string, fileName: string) => {
    setApplyPdfStyles(true);
    setTimeout(() => {
      Utils.generatePDF(id, fileName);
      setApplyPdfStyles(false);
    }, 0);
  };

  const inputRequiredBasicRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  // Q29 ---------------------------Q29-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------

  // Utility function to clean SCHOOL_PT1_Q29 data and ensure it's a proper object
  const cleanSchoolQ29Data = (data: any) => {
    if (!data) return {};

    // If it's an array, convert to empty object
    if (Array.isArray(data)) return {};

    // If it's an object, filter out numeric keys (array indices)
    if (typeof data === "object") {
      return Object.keys(data).reduce((acc, key) => {
        // Only keep non-numeric keys (ved names) and valid ved data
        if (isNaN(Number(key)) && data[key] && typeof data[key] === "object") {
          acc[key] = {
            selectedShakhas: Array.isArray(data[key].selectedShakhas)
              ? data[key].selectedShakhas
              : [],
            anyaDetails: data[key].anyaDetails || "",
          };
        }
        return acc;
      }, {} as any);
    }

    return {};
  };

  const onCheckboxChange = (ved: string, shakha: string, checked: boolean) => {
    const currentValues = form.getFieldValue("SCHOOL_PT1_Q29") || {};
    // Clean the current values to ensure proper structure
    const formattedCurrentValues = cleanSchoolQ29Data(currentValues);

    const vedSelections = formattedCurrentValues[ved] || {
      selectedShakhas: [],
      anyaDetails: "",
    };

    // Initialize selectedShakhas as an array if it doesn't exist
    const currentSelectedShakhas = Array.isArray(vedSelections.selectedShakhas)
      ? vedSelections.selectedShakhas
      : [];

    let updatedShakhas = [...currentSelectedShakhas];

    if (checked) {
      if (!updatedShakhas.includes(shakha)) {
        updatedShakhas.push(shakha);
      }
    } else {
      updatedShakhas = updatedShakhas.filter((item) => item !== shakha);
    }

    const updatedVedSelections: any = {
      selectedShakhas: updatedShakhas,
      anyaDetails: vedSelections.anyaDetails || "",
    };

    // Check if shakha is "ANYA" or contains "अन्य" for clearing anyaDetails
    const hasAnyaSelected = updatedShakhas.some(
      (s) => s === "ANYA" || s === "अन्य" || s.includes("अन्य")
    );

    if (!hasAnyaSelected) {
      updatedVedSelections.anyaDetails = "";
    }

    // Update the form values with proper object structure
    const updatedFormValues = {
      ...formattedCurrentValues,
      [ved]: updatedVedSelections,
    };

    form.setFieldsValue({
      SCHOOL_PT1_Q29: updatedFormValues,
    });

    handleFieldChange("SCHOOL_PT1_Q29", JSON.stringify(updatedFormValues));
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          year: 2025,
          userFormMappingId: ufmId,
          isPtOne: true,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.schoolPt1Forms || {}).every(
          (value) => value === null
        )
          ? {}
          : data?.schoolPt1Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields = [
          "SCHOOL_PT1_Q28_A",
          "SCHOOL_PT1_Q28_B",
          "SCHOOL_PT1_Q29",
          "SCHOOL_PT1_Q30",
          "SCHOOL_PT1_Q32",
          "SCHOOL_PT1_Q33",
          "SCHOOL_PT1_Q34",
        ];

        const fileFields = ["SCHOOL_PT1_Q43"];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                // Special handling for SCHOOL_PT1_Q29 to ensure it's an object
                if (key === "SCHOOL_PT1_Q29") {
                  acc[key] = formData[key] ? JSON.parse(formData[key]) : {};
                } else {
                  acc[key] = formData[key] ? JSON.parse(formData[key]) : [{}];
                }
              } catch (error) {
                console.error(error);
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {}
        );

        if (formattedData) {
          form.setFieldsValue(formattedData);
        }

        setDocumentsData(data?.documents);

        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId?.length && ufmId?.length) {
      getFormData();
    }
  }, [formId, ufmId]);

  const calculateColumnTotals = (
    form: any,
    fieldName: string,
    fieldsToSum: string[],
    totalField: string,
    totalRowIndex: number
  ) => {
    const currentData = form.getFieldValue(fieldName) || [];

    // Ensure currentData has the correct length
    if (currentData.length < totalRowIndex + 1) {
      // Fill missing rows with empty objects
      for (let i = currentData.length; i <= totalRowIndex; i++) {
        currentData[i] = {
          vedShakha: i === totalRowIndex ? "TOTAL_SUM" : "",
          fullTimeTeacher: "",
          partTimeTeacher: "",
          totalTeacher: "",
        };
      }
    }

    const totals: { [key: string]: number } = {};

    // Initialize totals
    fieldsToSum.forEach((field) => {
      totals[field] = 0;
    });

    // Calculate totals for each field
    currentData.forEach((row: any, index: number) => {
      if (index !== totalRowIndex) {
        fieldsToSum.forEach((field) => {
          totals[field] += parseInt(row[field] || 0);
        });
      }
    });

    // Update the total row
    const totalRow = currentData[totalRowIndex];
    if (totalRow) {
      fieldsToSum.forEach((field) => {
        totalRow[field] = totals[field].toString();
      });

      // Calculate the total for the total row
      const total = fieldsToSum.reduce((sum, field) => {
        return sum + parseInt(totalRow[field] || 0);
      }, 0);
      totalRow[totalField] = total.toString();

      // Update the form data
      form.setFieldsValue({ [fieldName]: currentData });
    }
  };

  const updateRowTotal = (
    form: any,
    fieldName: string,
    index: number,
    field: string,
    value: string,
    fieldsToSum: string[],
    totalField: string,
    totalRowIndex: number
  ) => {
    const currentData = form.getFieldValue(fieldName) || [];
    const row = currentData[index];

    if (row) {
      // Update the field value
      row[field] = value;

      // Calculate the total for the current row
      const total = fieldsToSum.reduce((sum, field) => {
        return sum + parseInt(row[field] || 0);
      }, 0);
      row[totalField] = total.toString();

      // Update the form data
      form.setFieldsValue({ [fieldName]: currentData });

      // Calculate the column-wise totals for the last row
      calculateColumnTotals(
        form,
        fieldName,
        fieldsToSum,
        totalField,
        totalRowIndex
      );
    }
  };

  // Q29 ---------------------------Q30-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------

  const DATA_SOURCE_SCHOOL_PT1_Q29: DATA_TYPE_SCHOOL_PT1_Q29[] = [
    {
      key: "1",
      label: "RIGVEDA",
      ved: Text({ tid: "STUDENT_LEVEL_RIGVED", def: "" }),
      vedShakha1: Text({ tid: "SHAKAL", def: "SHAKAL" }),
      vedShakha2: Text({ tid: "BASHKAL", def: "BASHKAL" }),
      vedShakha3: Text({ tid: "SHANKAYAN", def: "SHANKAYAN" }),
      vedShakha4: Text({ tid: "ANYA", def: "ANYA" }),
    },
    {
      key: "2",
      label: "SHUKLA YAJURVEDA",
      ved: Text({
        tid: "STUDENT_LEVEL_SUKLA_YAJURVED",
        def: "SUKLA_YAJURVED",
      }),
      vedShakha1: Text({ tid: "KANVA", def: "KANVA" }),
      vedShakha2: Text({ tid: "MADHYADINA", def: "MADHYADINA" }),
      vedShakha3: Text({ tid: "ANYA", def: "ANYA" }),
      vedShakha4: "",
    },
    {
      key: "3",
      label: "KRISHNA YAJURVEDA",
      ved: Text({
        tid: "STUDENT_LEVEL_KRISHNA_YAJURVED",
        def: "KRISHNA_YAJURVED",
      }),
      vedShakha1: Text({ tid: "TAITTIRIYA", def: "TAITTIRIYA" }),
      vedShakha2: Text({ tid: "MAITRAYANI", def: "MAITRAYANI" }),
      vedShakha3: Text({ tid: "ANYA", def: "ANYA" }),
      vedShakha4: "",
    },
    {
      key: "4",
      label: "SAMAVEDA",
      ved: Text({ tid: "STUDENT_LEVEL_SAMAVED", def: "SAMAVED" }),
      vedShakha1: Text({ tid: "KAUTHUMA", def: "KAUTHUMA" }),
      vedShakha2: Text({ tid: "JAIMINIYA", def: "JAIMINIYA" }),
      vedShakha3: Text({ tid: "RANAYANI", def: "RANAYANI" }),
      vedShakha4: Text({ tid: "ANYA", def: "ANYA" }),
    },
    {
      key: "5",
      label: "ATHARVAVEDA",
      ved: Text({ tid: "STUDENT_LEVEL_ATHARVAVED", def: "ATHARVAVED" }),
      vedShakha1: Text({ tid: "SHAUNAKA", def: "SHAUNAKA" }),
      vedShakha2: Text({ tid: "PIPPALADA", def: "PIPPALADA" }),
      vedShakha3: Text({ tid: "SHAKAL", def: "SHAKAL" }),
      vedShakha4: Text({ tid: "ANYA", def: "ANYA" }),
    },
  ];
  const TABLE_COLUMNS_SCHOOL_PT1_Q29: TableColumnsType<DATA_TYPE_SCHOOL_PT1_Q29> =
    [
      {
        dataIndex: "ved",
        render: (text) => <span className='bold-text'>{text}</span>,
      },
      ...DATA_SOURCE_SCHOOL_PT1_Q29.reduce(
        (columns: TableColumnsType<DATA_TYPE_SCHOOL_PT1_Q29>, row) => {
          // Get all vedShakha keys that exist in the current row
          const shakhaKeys = Object.keys(row).filter(
            (key) =>
              key.startsWith("vedShakha") &&
              row[key as keyof DATA_TYPE_SCHOOL_PT1_Q29] // Only include keys with non-empty values
          );

          // Add columns for vedShakha keys that haven't been added yet
          shakhaKeys.forEach((shakhaKey) => {
            if (
              !columns.some(
                (col) => "dataIndex" in col && col.dataIndex === shakhaKey
              )
            ) {
              columns.push({
                dataIndex: shakhaKey,
                render: (text: string, record: DATA_TYPE_SCHOOL_PT1_Q29) => {
                  const isOther = text === OTHER;
                  const formData = form.getFieldValue("SCHOOL_PT1_Q29") || {};

                  // Clean the form data to ensure proper structure
                  const formattedFormData = cleanSchoolQ29Data(formData);

                  const vedData = formattedFormData[record.label] || {
                    selectedShakhas: [],
                    anyaDetails: "",
                  };
                  const isChecked =
                    Array.isArray(vedData.selectedShakhas) &&
                    vedData.selectedShakhas.includes(text);

                  return text ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Form.Item valuePropName='checked'>
                        <Checkbox
                          disabled={disabled}
                          checked={isChecked}
                          onChange={(e) => {
                            onCheckboxChange(
                              record.label,
                              text,
                              e.target.checked
                            );
                            // If "ANYA" is unchecked, clear anyaDetails for this ved
                            if (isOther && !e.target.checked) {
                              const currentValues =
                                form.getFieldValue("SCHOOL_PT1_Q29") || {};
                              const formattedCurrentValues =
                                cleanSchoolQ29Data(currentValues);
                              const updatedValues = {
                                ...formattedCurrentValues,
                                [record.label]: {
                                  ...formattedCurrentValues[record.label],
                                  anyaDetails: "",
                                },
                              };
                              form.setFieldsValue({
                                SCHOOL_PT1_Q29: updatedValues,
                              });
                              handleFieldChange(
                                "SCHOOL_PT1_Q29",
                                JSON.stringify(updatedValues)
                              );
                            }
                          }}
                        >
                          {text}
                        </Checkbox>
                      </Form.Item>
                      {isOther && isChecked && (
                        <Form.Item
                          style={{ marginBottom: 0 }}
                          name={["SCHOOL_PT1_Q29", record.label, "anyaDetails"]}
                          rules={[
                            {
                              required: true,
                              message: Text({
                                tid: "REQUIRED",
                                def: "Required",
                              }),
                            },
                          ]}
                        >
                          <Input
                            placeholder=''
                            style={{ minWidth: "100px" }}
                            disabled={disabled}
                            value={vedData.anyaDetails || ""}
                            onChange={(e) => {
                              const currentValues =
                                form.getFieldValue("SCHOOL_PT1_Q29") || {};
                              const formattedCurrentValues =
                                cleanSchoolQ29Data(currentValues);
                              const updatedValues = {
                                ...formattedCurrentValues,
                                [record.label]: {
                                  ...formattedCurrentValues[record.label],
                                  anyaDetails: e.target.value,
                                },
                              };
                              form.setFieldsValue({
                                SCHOOL_PT1_Q29: updatedValues,
                              });
                            }}
                            onBlur={(e) => {
                              const currentValues =
                                form.getFieldValue("SCHOOL_PT1_Q29") || {};
                              const formattedCurrentValues =
                                cleanSchoolQ29Data(currentValues);
                              const updatedValues = {
                                ...formattedCurrentValues,
                                [record.label]: {
                                  ...formattedCurrentValues[record.label],
                                  anyaDetails: e.target.value,
                                },
                              };
                              form.setFieldsValue({
                                SCHOOL_PT1_Q29: updatedValues,
                              });
                              handleFieldChange(
                                "SCHOOL_PT1_Q29",
                                JSON.stringify(updatedValues)
                              );
                            }}
                            id={applyPdfStyles ? "affidavit_input" : ""}
                          />
                        </Form.Item>
                      )}
                    </div>
                  ) : null;
                },
              });
            }
          });

          return columns;
        },
        [] as TableColumnsType<DATA_TYPE_SCHOOL_PT1_Q29> // Initialize columns as an empty array
      ),
    ];

  // Q30 ---------------------------Q30-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q30RowCount = 13;

  const DATA_TYPE_SCHOOL_PT1_Q30_label = [
    Text({ tid: "RIGVEDA_SHAKAL", def: "RIGVEDA_SHAKAL" }),
    Text({ tid: "RIGVEDA_BASKAL", def: "RIGVEDA_BASKAL" }),
    Text({ tid: "RIGVEDA_SHANKHAYAN", def: "RIGVEDA_SHANKHAYAN" }),
    Text({
      tid: "SHUKLA_YAJURVEDA_KANVA",
      def: "SHUKLA_YAJURVEDA_KANVA",
    }),
    Text({
      tid: "SHUKLA_YAJURVEDA_MADHYANDINA",
      def: "SHUKLA_YAJURVEDA_MADHYANDINA",
    }),
    Text({
      tid: "KRISHNA_YAJURVEDA_TAITTIRIYA",
      def: "KRISHNA_YAJURVEDA_TAITTIRIYA",
    }),
    Text({
      tid: "KRISHNA_YAJURVEDA_MAITRAYANI",
      def: "KRISHNA_YAJURVEDA_MAITRAYANI",
    }),
    Text({ tid: "SAMAVEDA_KAUTHUM", def: "SAMAVEDA_KAUTHUM" }),
    Text({ tid: "SAMAVEDA_JAIMINIYA", def: "SAMAVEDA_JAIMINIYA" }),
    Text({ tid: "SAMAVEDA_RANAYANIYA", def: "SAMAVEDA_RANAYANIYA" }),
    Text({ tid: "ATHARVAVEDA_SHAUNAKA", def: "ATHARVAVEDA_SHAUNAKA" }),
    Text({
      tid: "ATHARVAVEDA_PAIPPALADA",
      def: "ATHARVAVEDA_PAIPPALADA",
    }),
    Text({ tid: "TOTAL_SUM", def: "TOTAL_SUM" }),
  ];

  const TABLE_COLUMNS_SCHOOL_PT1_Q30: TableProps<DATA_TYPE_SCHOOL_PT1_Q30>["columns"] =
    [
      {
        title: Text({ tid: "VEDASHAKHA", def: "VEDASHAKHA" }),
        dataIndex: "vedShakha",
        key: "vedShakha",
        render: (_, record, index) => {
          const labels = [
            Text({ tid: "RIGVEDA_SHAKAL", def: "RIGVEDA_SHAKAL" }),
            Text({ tid: "RIGVEDA_BASKAL", def: "RIGVEDA_BASKAL" }),
            Text({ tid: "RIGVEDA_SHANKHAYAN", def: "RIGVEDA_SHANKHAYAN" }),
            Text({
              tid: "SHUKLA_YAJURVEDA_KANVA",
              def: "SHUKLA_YAJURVEDA_KANVA",
            }),
            Text({
              tid: "SHUKLA_YAJURVEDA_MADHYANDINA",
              def: "SHUKLA_YAJURVEDA_MADHYANDINA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_TAITTIRIYA",
              def: "KRISHNA_YAJURVEDA_TAITTIRIYA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_MAITRAYANI",
              def: "KRISHNA_YAJURVEDA_MAITRAYANI",
            }),
            Text({ tid: "SAMAVEDA_KAUTHUM", def: "SAMAVEDA_KAUTHUM" }),
            Text({ tid: "SAMAVEDA_JAIMINIYA", def: "SAMAVEDA_JAIMINIYA" }),
            Text({ tid: "SAMAVEDA_RANAYANIYA", def: "SAMAVEDA_RANAYANIYA" }),
            Text({ tid: "ATHARVAVEDA_SHAUNAKA", def: "ATHARVAVEDA_SHAUNAKA" }),
            Text({
              tid: "ATHARVAVEDA_PAIPPALADA",
              def: "ATHARVAVEDA_PAIPPALADA",
            }),
            Text({ tid: "TOTAL_SUM", def: "TOTAL_SUM" }),
          ];
          return (
            labels[index] ||
            Text({ tid: "DEFAULT_LEVEL", def: "DEFAULT LEVEL" })
          );
        },
      },
      {
        title: Text({ tid: "FULL_TIME_TEACHER", def: "FULL_TIME_TEACHER" }),
        dataIndex: "fullTimeTeacher",
        key: "fullTimeTeacher",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q30", index, "fullTimeTeacher"]}
            rules={
              index === schoolPt1Q30RowCount - 1
                ? [] // No validation rules for TOTAL_SUM row
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q30RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q30",
                  index,
                  "fullTimeTeacher",
                  value,
                  ["fullTimeTeacher", "partTimeTeacher"],
                  "totalTeacher",
                  schoolPt1Q30RowCount - 1
                );
              }}
              onBlur={(e) => {
                // const value = e.target.value;

                handleFieldChange(
                  "SCHOOL_PT1_Q30",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q30"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "PART_TIME_TEACHER", def: "PART_TIME_TEACHER" }),
        dataIndex: "partTimeTeacher",
        key: "partTimeTeacher",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q30", index, "partTimeTeacher"]}
            rules={
              index === schoolPt1Q30RowCount - 1
                ? [] // No validation rules for TOTAL_SUM row
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q30RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q30",
                  index,
                  "partTimeTeacher",
                  value,
                  ["fullTimeTeacher", "partTimeTeacher"],
                  "totalTeacher",
                  schoolPt1Q30RowCount - 1
                );
              }}
              onBlur={(e) => {
                const value = e.target.value;

                handleFieldChange(
                  "SCHOOL_PT1_Q30",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q30"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TOTAL_TEACHER", def: "TOTAL_TEACHER" }),
        dataIndex: "totalTeacher",
        key: "totalTeacher",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q30", index, "totalTeacher"]}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input
              type='number'
              disabled
              value={record.totalTeacher || ""}
            />
          </Form.Item>
        ),
      },
    ];
  const DATA_SOURCE_SCHOOL_PT1_Q30: DATA_TYPE_SCHOOL_PT1_Q30[] = Array.from(
    { length: schoolPt1Q30RowCount },
    (_, index) => ({
      vedShakha: DATA_TYPE_SCHOOL_PT1_Q30_label[index],
      fullTimeTeacher: "",
      partTimeTeacher: "",
      totalTeacher: "",
    })
  );

  // Q32 ---------------------------Q32-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q32RowCount = 13;
  const TABLE_COLUMNS_SCHOOL_PT1_Q32: TableProps<DATA_TYPE_SCHOOL_PT1_Q32>["columns"] =
    [
      {
        title: Text({ tid: "VEDASHAKHA", def: "VEDASHAKHA" }),
        dataIndex: "vedShakha",
        key: "vedShakha",
        render: (_, record, index) => {
          const texts = [
            Text({ tid: "RIGVEDA_SHAKAL", def: "RIGVEDA_SHAKAL" }),
            Text({ tid: "RIGVEDA_BASKAL", def: "RIGVEDA_BASKAL" }),
            Text({ tid: "RIGVEDA_SHANKHAYAN", def: "RIGVEDA_SHANKHAYAN" }),
            Text({
              tid: "SHUKLA_YAJURVEDA_KANVA",
              def: "SHUKLA_YAJURVEDA_KANVA",
            }),
            Text({
              tid: "SHUKLA_YAJURVEDA_MADHYANDINA",
              def: "SHUKLA_YAJURVEDA_MADHYANDINA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_TAITTIRIYA",
              def: "KRISHNA_YAJURVEDA_TAITTIRIYA",
            }),
            Text({
              tid: "KRISHNA_YAJURVEDA_MAITRAYANI",
              def: "KRISHNA_YAJURVEDA_MAITRAYANI",
            }),
            Text({ tid: "SAMAVEDA_KAUTHUM", def: "SAMAVEDA_KAUTHUM" }),
            Text({ tid: "SAMAVEDA_JAIMINIYA", def: "SAMAVEDA_JAIMINIYA" }),
            Text({ tid: "SAMAVEDA_RANAYANIYA", def: "SAMAVEDA_RANAYANIYA" }),
            Text({ tid: "ATHARVAVEDA_SHAUNAKA", def: "ATHARVAVEDA_SHAUNAKA" }),
            Text({
              tid: "ATHARVAVEDA_PAIPPALADA",
              def: "ATHARVAVEDA_PAIPPALADA",
            }),
            Text({ tid: "TOTAL_SUM", def: "TOTAL_SUM" }),
          ];

          return (
            texts[index] || Text({ tid: "DEFAULT_LEVEL", def: "DEFAULT LEVEL" })
          );
        },
      },
      {
        title: Text({ tid: "MOOLANT_STUDENTS", def: "MOOLANT_STUDENTS" }),
        dataIndex: "moolantStudents",
        key: "moolantStudents",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q32", index, "moolantStudents"]}
            rules={
              index === schoolPt1Q32RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q32RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q32",
                  index,
                  "moolantStudents",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q32RowCount - 1
                );
              }}
              onBlur={(e) => {
                const value = e.target.value;

                handleFieldChange(
                  "SCHOOL_PT1_Q32",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q32"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_1_STUDENTS", def: "LEVEL_1_STUDENTS" }),
        dataIndex: "level1Students",
        key: "level1Students",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q32", index, "level1Students"]}
            rules={
              index === schoolPt1Q32RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q32RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q32",
                  index,
                  "level1Students",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q32RowCount - 1
                );
              }}
              onBlur={(e) => {
                handleFieldChange(
                  "SCHOOL_PT1_Q32",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q32"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_2_STUDENTS", def: "LEVEL_2_STUDENTS" }),
        dataIndex: "level2Students",
        key: "level2Students",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q32", index, "level2Students"]}
            rules={
              index === schoolPt1Q32RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q32RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q32",
                  index,
                  "level2Students",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q32RowCount - 1
                );
              }}
              onBlur={(e) => {
                handleFieldChange(
                  "SCHOOL_PT1_Q32",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q32"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "BEYOND_LEVEL_2_STUDENTS",
          def: "BEYOND_LEVEL_2_STUDENTS",
        }),
        dataIndex: "beyondLevel2Students",
        key: "beyondLevel2Students",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q32", index, "beyondLevel2Students"]}
            rules={
              index === schoolPt1Q32RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q32RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q32",
                  index,
                  "beyondLevel2Students",
                  value,
                  [
                    "moolantStudents",
                    "level1Students",
                    "level2Students",
                    "beyondLevel2Students",
                  ],
                  "totalVedStudents",
                  schoolPt1Q32RowCount - 1
                );
              }}
              onBlur={(e) => {
                const value = e.target.value;

                handleFieldChange(
                  "SCHOOL_PT1_Q32",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q32"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TOTAL_VED_STUDENTS", def: "TOTAL_VED_STUDENTS" }),
        dataIndex: "totalVedStudents",
        key: "totalVedStudents",
        render: (_, record, index) => (
          <Form.Item name={["SCHOOL_PT1_Q32", index, "totalVedStudents"]}>
            <Input
              type='number'
              disabled
              value={record.totalVedStudents || ""}
            />
          </Form.Item>
        ),
      },
    ];
  const DATA_SOURCE_SCHOOL_PT1_Q32: DATA_TYPE_SCHOOL_PT1_Q32[] = Array.from(
    { length: schoolPt1Q32RowCount },
    (_, index) => ({
      vedShakha: index === schoolPt1Q32RowCount - 1 ? "TOTAL_SUM" : "",
      moolantStudents: "",
      level1Students: "",
      level2Students: "",
      beyondLevel2Students: "",
      totalVedStudents: "",
    })
  );

  // Q33 ---------------------------Q33-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q33RowCount = 5;

  const TABLE_COLUMNS_SCHOOL_PT1_Q33: TableProps<DATA_TYPE_SCHOOL_PT1_Q33>["columns"] =
    [
      {
        title: Text({ tid: "YEAR", def: "YEAR" }),
        dataIndex: "year",
        key: "year",
      },
      {
        title: Text({
          tid: "STUDENTS_START_SESSION",
          def: "STUDENTS_START_SESSION",
        }),
        dataIndex: "studentStartSession",
        key: "studentStartSession",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q33", index, "studentStartSession"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input
              type='number'
              disabled={disabled}
              onBlur={(e) => {
                const formValues = form.getFieldValue("SCHOOL_PT1_Q33");
                handleFieldChange("SCHOOL_PT1_Q33", JSON.stringify(formValues));
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "STUDENTS_COMPLETED_STUDY",
          def: "STUDENTS_COMPLETED_STUDY",
        }),
        dataIndex: "studentCompletedStudy",
        key: "studentCompletedStudy",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q33", index, "studentCompletedStudy"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input
              type='number'
              disabled={disabled}
              onBlur={(e) => {
                const formValues = form.getFieldValue("SCHOOL_PT1_Q33");
                handleFieldChange("SCHOOL_PT1_Q33", JSON.stringify(formValues));
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "NEW_VEDIC_STUDENTS", def: "NEW_VEDIC_STUDENTS" }),
        dataIndex: "newVedicStudent",
        key: "newVedicStudent",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q33", index, "newVedicStudent"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input
              type='number'
              disabled={disabled}
              onBlur={(e) => {
                const formValues = form.getFieldValue("SCHOOL_PT1_Q33");
                handleFieldChange("SCHOOL_PT1_Q33", JSON.stringify(formValues));
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "STUDENTS_DROPPED_OUT",
          def: "STUDENTS_DROPPED_OUT",
        }),
        dataIndex: "studentDroppedOut",
        key: "studentDroppedOut",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q33", index, "studentDroppedOut"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input
              type='number'
              disabled={disabled}
              onBlur={(e) => {
                const formValues = form.getFieldValue("SCHOOL_PT1_Q33");
                handleFieldChange("SCHOOL_PT1_Q33", JSON.stringify(formValues));
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "STUDENTS_END_SESSION",
          def: "STUDENTS_END_SESSION",
        }),
        dataIndex: "studentEndSession",
        key: "studentEndSession",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q33", index, "studentEndSession"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
            })}
          >
            <Input
              type='number'
              disabled={disabled}
              onBlur={(e) => {
                const formValues = form.getFieldValue("SCHOOL_PT1_Q33");
                handleFieldChange("SCHOOL_PT1_Q33", JSON.stringify(formValues));
              }}
            />
          </Form.Item>
        ),
      },
    ];

  // Initial data source
  const DATA_SOURCE_SCHOOL_PT1_Q33: DATA_TYPE_SCHOOL_PT1_Q33[] = Array.from(
    { length: schoolPt1Q33RowCount },
    (_, index) => ({
      year: getNumberInLanguage(Number(new Date().getFullYear() - index)),
      studentStartSession: "",
      studentCompletedStudy: "",
      newVedicStudent: "",
      studentDroppedOut: "",
      studentEndSession: "",
    })
  );

  // Q34 ---------------------------Q34-TABLE-COLUMNS-AND-DATA-SOURCE---------------------------
  const schoolPt1Q34RowCount = 6;

  const TABLE_COLUMNS_SCHOOL_PT1_Q34: TableProps<DATA_TYPE_SCHOOL_PT1_Q34>["columns"] =
    [
      {
        title: Text({ tid: "LEVEL", def: "LEVEL" }),
        dataIndex: "level",
        key: "level",
        render: (_, record, index) => {
          const texts = [
            Text({ tid: "STUDENT_LEVEL_RIGVED", def: "SCHOOL_LEVEL_RIGVED" }),
            Text({
              tid: "STUDENT_LEVEL_SUKLA_YAJURVED",
              def: "SCHOOL_LEVEL_SUKLA_YAJURVED",
            }),
            Text({
              tid: "STUDENT_LEVEL_KRISHNA_YAJURVED",
              def: "SCHOOL_LEVEL_KRISHNA_YAJURVED",
            }),
            Text({
              tid: "STUDENT_LEVEL_SAMAVED",
              def: "SCHOOL_LEVEL_SAMAVED",
            }),
            Text({
              tid: "STUDENT_LEVEL_ATHARVAVED",
              def: "SCHOOL_LEVEL_ATHARVAVED",
            }),
            Text({ tid: "TOTAL_JOINED", def: "TOTAL_JOINED" }),
          ];
          return (
            texts[index] || Text({ tid: "DEFAULT_LEVEL", def: "DEFAULT LEVEL" })
          );
        },
      },
      {
        title: Text({ tid: "MOOLPATI", def: "MOOLPATI" }),
        dataIndex: "moolpathi",
        key: "moolpathi",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q34", index, "moolpathi"]}
            rules={
              index === schoolPt1Q34RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q34RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q34",
                  index,
                  "moolpathi",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q34RowCount - 1
                );
              }}
              onBlur={(e) => {
                handleFieldChange(
                  "SCHOOL_PT1_Q34",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q34"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_1", def: "LEVEL_1" }),
        dataIndex: "level1",
        key: "level1",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q34", index, "level1"]}
            rules={
              index === schoolPt1Q34RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q34RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q34",
                  index,
                  "level1",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q34RowCount - 1
                );
              }}
              onBlur={(e) => {
                handleFieldChange(
                  "SCHOOL_PT1_Q34",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q34"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_2", def: "LEVEL_2" }),
        dataIndex: "level2",
        key: "level2",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q34", index, "level2"]}
            rules={
              index === schoolPt1Q34RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q34RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q34",
                  index,
                  "level2",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q34RowCount - 1
                );
              }}
              onBlur={(e) => {
                handleFieldChange(
                  "SCHOOL_PT1_Q34",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q34"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "LEVEL_BEYOND_2", def: "LEVEL_BEYOND_2" }),
        dataIndex: "beyondLevel2",
        key: "beyondLevel2",
        render: (_, record, index) => (
          <Form.Item
            name={["SCHOOL_PT1_Q34", index, "beyondLevel2"]}
            rules={
              index === schoolPt1Q34RowCount - 1
                ? []
                : numberInputRules({
                    required: true,
                    allowNegative: false,
                    min: 0,
                  })
            }
          >
            <Input
              type='number'
              disabled={disabled || index === schoolPt1Q34RowCount - 1}
              onChange={(e) => {
                const value = e.target.value;
                updateRowTotal(
                  form,
                  "SCHOOL_PT1_Q34",
                  index,
                  "beyondLevel2",
                  value,
                  ["moolpathi", "level1", "level2", "beyondLevel2"],
                  "total",
                  schoolPt1Q34RowCount - 1
                );
              }}
              onBlur={(e) => {
                handleFieldChange(
                  "SCHOOL_PT1_Q34",
                  JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q34"))
                );
              }}
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TOTAL_JOINED", def: "TOTAL_JOINED" }),
        dataIndex: "total",
        key: "total",
        render: (_, record, index) => (
          <Form.Item name={["SCHOOL_PT1_Q34", index, "total"]}>
            <Input
              type='number'
              disabled
              value={record.total || ""}
            />
          </Form.Item>
        ),
      },
    ];

  const DATA_SOURCE_SCHOOL_PT1_Q34: DATA_TYPE_SCHOOL_PT1_Q34[] = Array.from(
    { length: schoolPt1Q34RowCount },
    (_, index) => ({
      level: index === schoolPt1Q34RowCount - 1 ? "TOTAL_JOINED" : "",
      moolpathi: "",
      level1: "",
      level2: "",
      beyondLevel2: "",
      total: "",
    })
  );
  // -------------------------------------------------------------------------------------------

  const LengthCounter: React.FC<{ form: any; fieldName: string }> = ({
    form,
    fieldName,
  }) => {
    // Use useWatch to get real-time updates for the field value
    const fieldValue = Form.useWatch(fieldName, form);

    return (
      <div className='length_counter'>
        {(fieldValue || "").length}/{INPUT_LENGTH.TEXTAREA_LENGTH}
      </div>
    );
  };

  useEffect(() => {
    if (formFor === eFORM_FOR.YOURSELF.toString()) {
      const autoFillData = {
        TEACHER_PT1_Q1:
          formFor === eFORM_FOR.YOURSELF.toString() ? user?.fullName : "",
        TEACHER_PT1_Q36:
          formFor === eFORM_FOR.YOURSELF.toString() ? user?.fullName : "",
        TEACHER_PT1_Q40:
          formFor === eFORM_FOR.YOURSELF.toString() ? user?.fullName : "",
        TEACHER_PT1_Q42:
          formFor === eFORM_FOR.YOURSELF.toString() ? user?.fullName : "",
        TEACHER_PT1_Q2: user?.aadhaarCardNumber,
        TEACHER_PT1_Q3: user?.dateOfBirth,
        TEACHER_PT1_Q5:
          formFor === eFORM_FOR.YOURSELF.toString() ? user?.fatherName : "",
        TEACHER_PT1_Q10: user?.mobileNumber,
        TEACHER_PT1_Q11: user?.emailId,
      };
      form.setFieldsValue(autoFillData);
      handleFieldChange("", "", autoFillData);
    }
  }, [formFor, user]);

 

  return (
    <div
      id='schoolFormPart1'
      className={`${styles.formWrapper} studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name='register'
        onFinish={() => handleFieldChange("", "", null, true)}
        initialValues={{}}
        scrollToFirstError
        layout='vertical'
      >
        {/* FORM HEADING */}
        <Row>
          <Typography.Title level={3}>
            {Text({
              tid: "SCHOOL_PART_1_FORM_HEADING",
              def: "SCHOOL_PART_1_FORM_HEADING",
            })}
          </Typography.Title>
          {!disabled && (
            <Typography.Text type='danger'>
              {Text({
                tid: "all_fields_are_mandatory",
                def: "All fields are mandatory!",
              })}
            </Typography.Text>
          )}
        </Row>
        <Row>
          <Typography.Text>
            {Text({
              tid: "SCHOOL_PT1_HEADING_Q1",
              def: "Name and full address of the Ved school:",
            })}
          </Typography.Text>
        </Row>
        {/* 1. Name of Vedvidhyalaya and full address */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q1_NAME'
              label={Text({
                tid: "SCHOOL_PT1_Q1_NAME",
                def: "Name of Vedvidhyalaya and full address",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q1_NAME", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='SCHOOL_PT1_Q1_ADDRESS'
              label={Text({
                tid: "SCHOOL_PT1_Q1_ADDRESS",
                def: "Full address of Vedvidhyalaya",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q1_ADDRESS", e.target.value);
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q1_ADDRESS", e.target.value);
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q1_ADDRESS'
            />
          </Col>
        </Row>
        {/* HEADING Qestion 2*/}
        <Row>
          <Typography.Text>
            {Text({
              tid: "SCHOOL_PT1_HEADING_Q2",
              def: "Information about Head of the Institution",
            })}
          </Typography.Text>
        </Row>
        {/* 2. Name */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q2'
              label={Text({
                tid: "SCHOOL_PT1_Q2",
                def: "Name",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) => {
                  handleFieldChange("SCHOOL_PT1_Q2", e.target.value);
                  form.setFieldValue("SCHOOL_PT1_Q40", e.target.value);
                  handleFieldChange("SCHOOL_PT1_Q40", e.target.value);
                  form.setFieldValue("SCHOOL_PT1_Q42", e.target.value);
                  handleFieldChange("SCHOOL_PT1_Q42", e.target.value);
                }}
                onChange={(e) => {
                  form.setFieldValue("SCHOOL_PT1_Q40", e.target.value);
                  handleFieldChange("SCHOOL_PT1_Q40", e.target.value);
                  form.setFieldValue("SCHOOL_PT1_Q42", e.target.value);
                  handleFieldChange("SCHOOL_PT1_Q42", e.target.value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 3. Full address */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='SCHOOL_PT1_Q3'
              label={Text({
                tid: "SCHOOL_PT1_Q3",
                def: "Full address",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q3", e.target.value);
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q3", e.target.value);
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q3'
            />
          </Col>
        </Row>
        {/* 4. Mobile No. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q4'
              label={Text({
                tid: "SCHOOL_PT1_Q4",
                def: "Mobile No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: Regex.MOBILENO, // Updated pattern
                  message: Text({
                    tid: "mobile_number_validation",
                    def: "Enter a valid 10-digit mobile number",
                  }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q4", e.target.value)
                }
                maxLength={10}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 5. Email ID */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q5'
              label={Text({
                tid: "SCHOOL_PT1_Q5",
                def: "Email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },

                {
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: Text({
                    tid: "email_validation",
                    def: "Enter a valid email address",
                  }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q5", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 6. Website */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q6'
              label={Text({
                tid: "SCHOOL_PT1_Q6",
                def: "Website",
              })}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q6", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* HEADING Qestion 7*/}
        <Row>
          <Typography.Text>
            {Text({
              tid: "SCHOOL_PT1_Q7_HEADING",
              def: "",
            })}
          </Typography.Text>
        </Row>
        {/* 7. Name */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q7'
              label={Text({
                tid: "SCHOOL_PT1_Q7",
                def: "Name",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q7", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 8. Full address */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q8'
              label={Text({
                tid: "SCHOOL_PT1_Q8",
                def: "Full address",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q8", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 9. Mobile No. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q9'
              label={Text({
                tid: "SCHOOL_PT1_Q9",
                def: "Mobile No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: Regex.MOBILENO, // Updated pattern
                  message: Text({
                    tid: "mobile_number_validation",
                    def: "Enter a valid 10-digit mobile number",
                  }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q9", e.target.value)
                }
                maxLength={10}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 10. Email ID */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q10'
              label={Text({
                tid: "SCHOOL_PT1_Q10",
                def: "Email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: Text({
                    tid: "VALID_EMAIL",
                    def: "VALID_EMAIL",
                  }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q10", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 11. Language */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='SCHOOL_PT1_Q11'
              label={Text({
                tid: "SCHOOL_PT1_Q11",
                def: "In which language should the relevant conversation be conducted with you?",
              })}
              rules={inputRequiredBasicRule}
            >
              <Radio.Group
                disabled={disabled}
                onChange={(e) =>
                  handleFieldChange(
                    "SCHOOL_PT1_Q11",
                    (e.target as HTMLInputElement).value
                  )
                }
              >
                <Radio value='English'>
                  {Text({ tid: "ENGLISH", def: "English" })}
                </Radio>
                <Radio value='हिन्दी'>
                  {Text({ tid: "HINDI", def: "Hindi" })}
                </Radio>
                <Radio value='संस्कृतम्'>
                  {Text({ tid: "SANSKRIT", def: "Sanskrit" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {/* HEADING Qestion 12*/}
        <Row>
          <Typography.Text>
            {Text({
              tid: "SCHOOL_PT1_Q12",
              def: "",
            })}
          </Typography.Text>
        </Row>
        <Row className='QuestionsSubSection'>
          <Typography.Text>
            {Text({
              tid: "SCHOOL_PT1_Q13_HEADING",
              def: "",
            })}
          </Typography.Text>
        </Row>
        {/* 13. Name */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q13'
              label={Text({
                tid: "SCHOOL_PT1_Q13",
                def: "Name",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q13", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 14. Registration No. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q14'
              label={Text({
                tid: "SCHOOL_PT1_Q14",
                def: "Registration No.",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q14", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 15. Name of Chief Trustee. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q15'
              label={Text({
                tid: "SCHOOL_PT1_Q15",
                def: "Name of Chief Trustee",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q15", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 16. Mobile No. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q16'
              label={Text({
                tid: "SCHOOL_PT1_Q16",
                def: "Mobile No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q16", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 17. Email ID */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q17'
              label={Text({
                tid: "SCHOOL_PT1_Q17",
                def: "Email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q17", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* HEADING Qestion 18*/}
        <Row className='QuestionsSubSection'>
          <Typography.Text>
            {Text({
              tid: "SCHOOL_PT1_Q18_HEADING",
              def: "",
            })}
          </Typography.Text>
        </Row>
        {/* 18. Name of Sponsor */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q18'
              label={Text({
                tid: "SCHOOL_PT1_Q18",
                def: "Name of Sponsor",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q18", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 19. Full address of founder */}
        <Row className='QuestionsSubSection'>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q19'
              label={Text({
                tid: "SCHOOL_PT1_Q19",
                def: "Full address of founder",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q19", e.target.value);
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q19", e.target.value);
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q19'
            />
          </Col>
        </Row>
        {/* 20. Name of Sponsor Institution Head */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q20'
              label={Text({
                tid: "SCHOOL_PT1_Q20",
                def: "Name of Sponsor Institution Head",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q20", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 21. Mobile No. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q21'
              label={Text({
                tid: "SCHOOL_PT1_Q21",
                def: "Mobile No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q21", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 22. Email ID */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q22'
              label={Text({
                tid: "SCHOOL_PT1_Q22",
                def: "Email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q22", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* HEADING Qestion 23*/}
        <Row className='QuestionsSubSection'>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_Q23_HEADING",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 23. Name of Chief Founde */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q23'
              label={Text({
                tid: "SCHOOL_PT1_Q23",
                def: "Name of Chief Founde",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q23", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 24. Address of Sponsor */}
        <Row className='QuestionsSubSection'>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q24'
              label={Text({
                tid: "SCHOOL_PT1_Q24",
                def: "Address of Sponsor",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q24", e.target.value);
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q24", e.target.value);
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q24'
            />
          </Col>
        </Row>
        {/* 25. Mobile No. */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q25'
              label={Text({
                tid: "SCHOOL_PT1_Q25",
                def: "Mobile No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q25", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 26. Email ID */}
        <Row className='QuestionsSubSection'>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q26'
              label={Text({
                tid: "SCHOOL_PT1_Q26",
                def: "Email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q26", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 27. Date of commencement of teaching in the Ved school  */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='SCHOOL_PT1_Q27'
              label={Text({
                tid: "SCHOOL_PT1_Q27",
                def: "SCHOOL_PT1_Q27",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                type='date'
                max={disableFutureDates_on_input_date_max()}
                disabled={disabled}
                onClick={(e) => e.currentTarget.showPicker()}
                onBlur={(e) =>
                  handleFieldChange("SCHOOL_PT1_Q27", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 28. Has the Ved school ever been completely closed for any period? If yes, from when to when? */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='SCHOOL_PT1_Q28'
              label={Text({
                tid: "SCHOOL_PT1_Q28",
                def: "Has the Ved school ever been completely closed for any period? If yes, from when to when?",
              })}
              rules={inputRequiredBasicRule}
            >
              <Radio.Group
                disabled={disabled}
                onChange={(e) => {
                  if (e.target.value === "No") {
                    form.setFieldsValue({
                      SCHOOL_PT1_Q28_A: "",
                      SCHOOL_PT1_Q28_B: "",
                    });
                  }

                  handleFieldChange(
                    "SCHOOL_PT1_Q28",
                    (e.target as HTMLInputElement).value
                  );
                }}
              >
                <Radio value={eYES_OR_NO.YES}>
                  {Text({ tid: "YES", def: "Yes" })}
                </Radio>
                <Radio value={eYES_OR_NO.NO}>
                  {Text({ tid: "NO", def: "No" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {/*  starting date and ending date */}
        {form.getFieldValue("SCHOOL_PT1_Q28") === eYES_OR_NO.YES && (
          <Row gutter={30}>
            <Col
              sm={12}
              lg={6}
              xl={4}
            >
              <Form.Item
                className='w-full'
                name='SCHOOL_PT1_Q28_A'
                rules={inputRequiredBasicRule}
              >
                <Input
                  type='month'
                  disabled={disabled}
                  onClick={(e) => e.currentTarget.showPicker()}
                  onChange={() =>
                    handleFieldChange(
                      "SCHOOL_PT1_Q28_A",
                      JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q28_A"))
                    )
                  }
                />
              </Form.Item>
            </Col>
            <Col
              sm={12}
              lg={6}
              xl={4}
            >
              <Form.Item
                className='w-full'
                name='SCHOOL_PT1_Q28_B'
                rules={inputRequiredBasicRule}
              >
                <Input
                  type='month'
                  disabled={disabled}
                  onClick={(e) => e.currentTarget.showPicker()}
                  onChange={() =>
                    handleFieldChange(
                      "SCHOOL_PT1_Q28_B",
                      JSON.stringify(form.getFieldValue("SCHOOL_PT1_Q28_B"))
                    )
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* 29). QUESTION HEADING */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_Q29",
                def: "SCHOOL_PT1_Q29",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 29). INPUT TABLE DUE*/}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q29'
              className='checkboxTable'
            >
              <Table<DATA_TYPE_SCHOOL_PT1_Q29>
                columns={TABLE_COLUMNS_SCHOOL_PT1_Q29}
                scroll={{ x: "80vw" }}
                dataSource={DATA_SOURCE_SCHOOL_PT1_Q29}
                showHeader={false}
                pagination={false}
                // style={{ width: "100%" }}
                size='large'
                id='school-pt1-q29-table'
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 30). QUESTION HEADING */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_Q30",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 30). INPUT TABLE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT1_Q30'>
              <Table<DATA_TYPE_SCHOOL_PT1_Q30>
                columns={TABLE_COLUMNS_SCHOOL_PT1_Q30}
                scroll={{ x: "80vw" }}
                dataSource={DATA_SOURCE_SCHOOL_PT1_Q30}
                pagination={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 31. Name of Chief Founde */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q31'
              label={Text({
                tid: "SCHOOL_PT1_Q31",
                def: "Name of Chief Founde",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q31", e.target.value);
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q31", e.target.value);
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q31'
            />
          </Col>
        </Row>
        {/* 32). QUESTION HEADING*/}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_Q32_HEADING",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 32). INPUT TABLE  */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q32'
              label={Text({
                tid: "SCHOOL_PT1_Q32",
                def: "Count of Current Students",
              })}
            >
              <Table<DATA_TYPE_SCHOOL_PT1_Q32>
                columns={TABLE_COLUMNS_SCHOOL_PT1_Q32}
                size='large'
                scroll={{ x: "80vw" }}
                dataSource={DATA_SOURCE_SCHOOL_PT1_Q32}
                pagination={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 33). QUESTION HEADING */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_Q33",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 33). INPUT TABLE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT1_Q33'>
              <Table<DATA_TYPE_SCHOOL_PT1_Q33>
                columns={TABLE_COLUMNS_SCHOOL_PT1_Q33}
                size='large'
                scroll={{ x: "80vw" }}
                dataSource={DATA_SOURCE_SCHOOL_PT1_Q33}
                pagination={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 34). QUESTION HEADING */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_Q34",
                def: "",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 34). INPUT TABLE */}
        <Row>
          <Col xs={24}>
            <Form.Item name='SCHOOL_PT1_Q34'>
              <Table<DATA_TYPE_SCHOOL_PT1_Q34>
                columns={TABLE_COLUMNS_SCHOOL_PT1_Q34}
                size='large'
                scroll={{ x: "80vw" }}
                dataSource={DATA_SOURCE_SCHOOL_PT1_Q34}
                pagination={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 35. Purpose of establishing the Ved school */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q35'
              label={Text({
                tid: "SCHOOL_PT1_Q35",
                def: "Purpose of establishing the Ved school",
              })}
              rules={inputRequiredBasicRule}
              style={{ position: "relative" }} // Relative positioning for the container
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q35", e.target.value); // Handle blur event
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q35", e.target.value); // Update form value in real-time
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q35'
            />
          </Col>
        </Row>
        {/* 36. Any special achievements of the institution in the field of Ved propagation or preservation that make it worthy of this award. */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q36'
              label={Text({
                tid: "SCHOOL_PT1_Q36",
                def: "Any special achievements of the institution in the field of Ved propagation or preservation that make it worthy of this award.",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("SCHOOL_PT1_Q36", e.target.value)
                  }
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q36", e.target.value); // Update form value in real-time
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q36'
            />
          </Col>
        </Row>
        {/* 37. Provide details of initiatives undertaken for public awareness regarding the Vedas (Limit your answer to four lines). */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q37'
              label={Text({
                tid: "SCHOOL_PT1_Q37",
                def: "Provide details of initiatives undertaken for public awareness regarding the Vedas (Limit your answer to four lines).",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q37", e.target.value); // Handle blur event
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q37", e.target.value); // Update form value in real-time
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q37'
            />
          </Col>
        </Row>
        {/* 38. Mention three achievements of your Ved school that make you proud (Limit your answer to four lines). */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q38'
              label={Text({
                tid: "SCHOOL_PT1_Q38",
                def: "Mention three achievements of your Ved school that make you proud (Limit your answer to four lines).",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q38", e.target.value); // Handle blur event
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q38", e.target.value); // Update form value in real-time
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q38'
            />
          </Col>
        </Row>
        {/* 39. Provide details of Ved-related programs (Parayan, Swahakar, etc.) conducted in the past five years (Limit your answer to four lines). */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='SCHOOL_PT1_Q39'
              label={Text({
                tid: "SCHOOL_PT1_Q39",
                def: "Provide details of Ved-related programs (Parayan, Swahakar, etc.) conducted in the past five years (Limit your answer to four lines).",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled}
                  onBlur={(e) => {
                    handleFieldChange("SCHOOL_PT1_Q39", e.target.value); // Handle blur event
                  }}
                  onChange={(e) => {
                    form.setFieldValue("SCHOOL_PT1_Q39", e.target.value); // Update form value in real-time
                  }}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH} // Set the maximum character limit
                  style={{ paddingBottom: 20 }} // Add padding to avoid overlap with counter
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='SCHOOL_PT1_Q39'
            />
          </Col>
        </Row>
        {/* Affidavit Title */}
        <div
          id='school_form1_ceo_affidavit'
          className='affidavitSection'
        >
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "SCHOOL_PT1_AFFIDAVIT_HEADING",
                  def: "SCHOOL_PT1_AFFIDAVIT_HEADING",
                })}
              </Typography.Text>
            </Col>
          </Row>
          {/* 40-41. head institute name and years of teaching ved  Affidavit Declaration  */}
          <Row className='affidavitRow'>
            <Col xs={24}>
              <Typography.Text type='secondary'>
                {Text({
                  tid: "SCHOOL_PT1_AFFIDAVIT_DECLARATION_1",
                  def: "I, (Full name of the Head of the Institution)",
                })}
              </Typography.Text>
              <Form.Item
                name='SCHOOL_PT1_Q40'
                rules={inputRequiredBasicRule}
                className='affidavitRowInput'
              >
                <Input
                  disabled={applyPdfStyles ? false : true}
                  onBlur={(e) =>
                    handleFieldChange("SCHOOL_PT1_Q40", e.target.value)
                  }
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
              <Typography.Text type='secondary'>
                {Text({
                  tid: "SCHOOL_PT1_AFFIDAVIT_DECLARATION_2",
                  def: "solemnly declare that:",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <ul className='mb-10'>
            <li>
              <Row className='affidavitRow'>
                <Col xs={24}>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "SCHOOL_PT1_AFFIDAVIT_VED_TEACHING_1",
                      def: "Ved teaching has been continuously conducted in this Ved school for the past",
                    })}
                  </Typography.Text>
                  <Form.Item
                    name='SCHOOL_PT1_Q41'
                    rules={numberInputRules({
                      required: true,
                      allowNegative: false,
                      min: 0,
                      max: 120,
                    })}
                    className='affidavitRowInput'
                  >
                    <Input
                      disabled={disabled}
                      onBlur={(e) =>
                        handleFieldChange("SCHOOL_PT1_Q41", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "SCHOOL_PT1_AFFIDAVIT_VED_TEACHING_2",
                      def: "years.",
                    })}
                  </Typography.Text>
                </Col>
              </Row>
            </li>
            <Row>
              <Col>
                <li>
                  <Typography.Text>
                    {Text({
                      tid: "SCHOOL_PT1_AFFIDAVIT_INFORMATION_ACCURACY",
                      def: "All the information provided in this application form is completely true.",
                    })}
                  </Typography.Text>
                </li>
              </Col>
              <Col>
                <li>
                  <Typography.Text>
                    {Text({
                      tid: "SCHOOL_PT1_AFFIDAVIT_DOCUMENTS_AUTHENTICITY",
                      def: "All the information in the attached agreements and certificates with this application is true.",
                    })}
                  </Typography.Text>
                </li>
              </Col>
            </Row>
          </ul>
          <Row>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_AFFIDAVIT_SIGN_CONFIRMATION",
                def: "I have signed this certificate after verifying all the facts.",
              })}
            </Typography.Text>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_AFFIDAVIT_UNDERSTANDING",
                def: "I fully understand the significance and implications of signing this affidavit.",
              })}
            </Typography.Text>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_PT1_AFFIDAVIT_DISQUALIFICATION_CONSENT",
                def: "I acknowledge that if any information in this affidavit is found to be false, this Vedic school, along with all its teachers and students, may be permanently disqualified from the Bharatatma Puraskar.",
              })}
            </Typography.Text>
          </Row>
          {/* 42) Full Name of the Head of the Institution*/}
          <Row className='QuestionsSubSection affidavitQuestionFirst'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "SCHOOL_PT1_Q42",
                      def: "Full Name of the Head of the Institution",
                    })}
                  </span>
                  <Form.Item
                    name='SCHOOL_PT1_Q42'
                    rules={inputRequiredBasicRule}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={false}
                      onBlur={(e) =>
                        handleFieldChange("SCHOOL_PT1_Q42", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='SCHOOL_PT1_Q42'
                  label={Text({
                    tid: "SCHOOL_PT1_Q42",
                    def: "Full Name of the Head of the Institution",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <Input
                    disabled={applyPdfStyles ? false : true}
                    onBlur={(e) =>
                      handleFieldChange("SCHOOL_PT1_Q42", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {/* 44) Date */}
          <Row className='QuestionsSubSection'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "SCHOOL_PT1_Q44",
                      def: "Date",
                    })}
                  </span>
                  <Form.Item
                    name='SCHOOL_PT1_Q44'
                    rules={inputRequiredBasicRule}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      type={undefined}
                      disabled={false}
                      onChange={(e) =>
                        handleFieldChange("SCHOOL_PT1_Q44", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='SCHOOL_PT1_Q44'
                  label={Text({
                    tid: "SCHOOL_PT1_Q44",
                    def: "Date",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <Input
                    type={applyPdfStyles ? undefined : "date"}
                    disabled={disabled}
                    max={disableFutureDates_on_input_date_max()}
                    onChange={(e) =>
                      handleFieldChange("SCHOOL_PT1_Q44", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          {/* 45) Place*/}
          <Row className='QuestionsSubSection'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "SCHOOL_PT1_Q45",
                      def: "Place",
                    })}
                  </span>
                  <Form.Item
                    name='SCHOOL_PT1_Q45'
                    rules={inputRequiredBasicRule}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={false}
                      onBlur={(e) =>
                        handleFieldChange("SCHOOL_PT1_Q45", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='SCHOOL_PT1_Q45'
                  label={Text({
                    tid: "SCHOOL_PT1_Q45",
                    def: "Place",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <Input
                    disabled={disabled}
                    onBlur={(e) =>
                      handleFieldChange("SCHOOL_PT1_Q45", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {applyPdfStyles ? (
            <Row className='QuestionsSubSection'>
              <Col
                xs={24}
                lg={12}
                xl={8}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "SCHOOL_PT1_Q43",
                      def: "Signature of the Head of the Institution",
                    })}
                  </span>
                  <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                    <Input id={applyPdfStyles ? "affidavit_input" : ""} />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          ) : null}
        </div>

        <div className='affidavitSection'>
          {!disabled && (
            <Row className='mb-15 QuestionsSubSection'>
              <Button
                className='downloadAffidavitButton'
                type='default'
                onClick={() =>
                  handleGeneratePDF(
                    "school_form1_ceo_affidavit",
                    "school_form1_ceo_affidavit"
                  )
                }
              >
                {Text({
                  tid: "DOWNLOAD_AFFIDAVIT",
                  def: "Download affidavit",
                })}
              </Button>
            </Row>
          )}
          {/* 43) Signature of the Head of the Institution*/}
          <Row className='QuestionsSubSection'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='SCHOOL_PT1_Q43'
                label={Text({
                  tid: "UPLOAD_AFFIDAVIT",
                  def: "UPload affidavit",
                })}
                rules={[
                  {
                    required:
                      signatureId ||
                      (documentsData?.length &&
                        documentsData.find(
                          (doc: any) => doc.labelName === "SCHOOL_PT1_Q43"
                        ))
                        ? false
                        : true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  type='file'
                  accept='.jpg,.jpeg,.png,.pdf'
                  disabled={disabled}
                  // onChange={(e) => {
                  //   if (e.target.files && e.target.files[0]) {
                  //     uploadDocument(
                  //       e.target.files[0],
                  //       eDocType.OTHER,
                  //       "SCHOOL_PT1_Q43"
                  //     );
                  //   }
                  // }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!validateFile(file as File).valid) {
                      onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.leftBottom,
                        content: validateFile(file as File).error,
                      });
                      e.target.value = "";
                      form.resetFields(["SCHOOL_PT1_Q43"]);
                    } else if (file) {
                      uploadDocument(file, eDocType.OTHER, "SCHOOL_PT1_Q43");
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              {signatureId ? (
                <div className='previewLink'>
                  <span>
                    {Text({
                      tid: "ATTACHED_DOCUMENT",
                      def: "Attached Document",
                    })}
                    :
                  </span>
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${signatureId}`}
                    target='_blank'
                    rel='noreferrer'
                    className='primary-link'
                  >
                    {PREVIEW}
                  </a>
                </div>
              ) : (
                documentsData &&
                documentsData.length > 0 &&
                documentsData
                  .filter((doc: any) => doc.labelName === "SCHOOL_PT1_Q43")
                  .map((doc: any) => (
                    <div className='previewLink'>
                      <span>
                        {Text({
                          tid: "ATTACHED_DOCUMENT",
                          def: "Attached Document",
                        })}
                        :
                      </span>
                      <a
                        href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${doc.docPath}`}
                        target='_blank'
                        rel='noreferrer'
                        className='primary-link'
                      >
                        {doc.docName}
                      </a>
                    </div>
                  ))
              )}
            </Col>
          </Row>
        </div>

        {/* -------------------------------------------- */}
        {/* SUBMIT BUTTON */}
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && (
            <Button
              block
              type='primary'
              // variant="filled"
              htmlType='submit'
              disabled={isLoading || disabled || isUploading}
              className='formSubmitButton'
              loading={isSubmitting}
            >
              {Text({ tid: "SUBMIT", def: "SUBMIT" })}
            </Button>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default SchoolPartOne;
